/**
 * comfyOutputService.js
 * Service layer for ComfyOutput media operations.
 *
 * All functions use ipcRenderer.invoke (request-response pattern) to communicate
 * with the main process. This eliminates the race conditions that existed with
 * the old send/on/once streaming pattern where multiple listeners would fight
 * over the same IPC channels.
 *
 * IPC Channel Naming Convention: All channels use 'comfy:' prefix.
 */

import electronBridge from '../utils/electronBridge';
const ipcRenderer = electronBridge.isElectron ? electronBridge.ipcRenderer : null;

const ensureElectron = () => {
  if (!ipcRenderer) {
    throw new Error('comfyOutputService is only available in Electron');
  }
};

/**
 * Fetch a page of media items from the ComfyUI output database.
 * Returns items with thumbnail file paths (not base64) for performance.
 *
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (1-based)
 * @param {number} options.pageSize - Items per page (default 40)
 * @param {string} options.category - Filter: "all", "image", "video", "audio"
 * @param {string} options.sortMethod - Sort: "newest" or "oldest"
 * @returns {Promise<{success: boolean, data: {items: Array, totalCount: number, page: number, pageSize: number, hasMore: boolean}, error?: string}>}
 */
export const getMediaItems = async ({ page = 1, pageSize = 40, category = 'all', sortMethod = 'newest' } = {}) => {
  try {
    ensureElectron();
    return await ipcRenderer.invoke('comfy:getMedia', { page, pageSize, category, sortMethod });
  } catch (error) {
    console.error('[ComfyOutputService] Error fetching media items:', error);
    return { success: false, data: { items: [], totalCount: 0, page, pageSize, hasMore: false }, error: error.message };
  }
};

/**
 * Get the total count of media items, optionally filtered by category.
 * Uses a dedicated SELECT COUNT(*) query — no data fetching involved.
 *
 * @param {string} category - Filter: "all", "image", "video", "audio"
 * @returns {Promise<{success: boolean, count: number, error?: string}>}
 */
export const getMediaCount = async (category = 'all') => {
  try {
    ensureElectron();
    return await ipcRenderer.invoke('comfy:getCount', { category });
  } catch (error) {
    console.error('[ComfyOutputService] Error fetching media count:', error);
    return { success: false, count: 0, error: error.message };
  }
};

/**
 * Fetch the full-size image data (base64) for detail/zoom view.
 * Only used when viewing a single item — not for gallery thumbnails.
 *
 * @param {string} hash - Unique hash of the media item
 * @returns {Promise<{success: boolean, image?: Object, error?: string}>}
 */
export const getFullImage = async (hash) => {
  try {
    ensureElectron();
    return await ipcRenderer.invoke('comfy:getFullImage', { hash });
  } catch (error) {
    console.error('[ComfyOutputService] Error fetching full image:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a media item by hash. Removes both the file from disk and the DB record.
 *
 * @param {string} hash - Unique hash of the media item
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteMedia = async (hash) => {
  try {
    ensureElectron();
    return await ipcRenderer.invoke('comfy:deleteMedia', { hash });
  } catch (error) {
    console.error('[ComfyOutputService] Error deleting media:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update metadata for a media item (e.g. inCollection, tag).
 *
 * @param {string} hash - Unique hash of the media item
 * @param {Object} updates - Fields to update { inCollection?: boolean, tag?: string }
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateMedia = async (hash, updates) => {
  try {
    ensureElectron();
    return await ipcRenderer.invoke('comfy:updateMedia', { hash, updates });
  } catch (error) {
    console.error('[ComfyOutputService] Error updating media:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Rebuild the media database by re-scanning the output directory.
 * Adds missing files and removes orphaned DB entries.
 *
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const rebuildDatabase = async () => {
  try {
    ensureElectron();
    return await ipcRenderer.invoke('comfy:rebuildDatabase');
  } catch (error) {
    console.error('[ComfyOutputService] Error rebuilding database:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get the ComfyUI output directory path from the main process.
 * Used to construct localfile:// URLs for direct media access.
 *
 * @returns {Promise<string|null>}
 */
export const getOutputPath = async () => {
  try {
    ensureElectron();
    return await ipcRenderer.invoke('comfy:getOutputPath');
  } catch (error) {
    console.error('[ComfyOutputService] Error getting output path:', error);
    return null;
  }
};
